import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Grid,
  IconButton,
  Tabs,
  Tab,
  Autocomplete,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Flag as GoalIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  AccountBalance as FinanceIcon,
  List as ListIcon,
  RateReview as ReviewIcon
} from '@mui/icons-material';
import incomeService from '../../income/services/incomeService';
import { useAuthContext } from '../../auth/context/AuthContext';
import MobileViewSelector from '../../../components/shared/MobileViewSelector';
import { UserIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import savingsService from '../../../services/savingsService';
import expenseService from '../../../services/expenseService';
import debtService from '../../../services/debtService';
import walletService from '../../../features/wallet/services/walletService';
import toast from 'react-hot-toast';

// Journey name generation (simple version for frontend)
const generateJourneyName = () => {
  const names = [
    'Path to Prosperity',
    'Financial Freedom Journey',
    'Building Wealth Together',
    'Steady Progress Plan',
    'Road to Success',
    'Wealth Building Adventure',
    'Financial Independence Quest',
    'Prosperity Pathway',
    'Smart Savings Strategy',
    'Future Financial Security'
  ];
  return names[Math.floor(Math.random() * names.length)];
};

const STEPS = [
  { label: 'Goals', icon: GoalIcon },
  { label: 'Personal Info', icon: PersonIcon },
  { label: 'Income & Career', icon: MoneyIcon },
  { label: 'Financials', icon: FinanceIcon },
  { label: 'Goal Details', icon: ListIcon },
  { label: 'Review', icon: ReviewIcon }
];

// Goal options
const GOAL_OPTIONS = [
  { id: 'emergency_fund', label: 'Build Emergency Fund', icon: '🛡️' },
  { id: 'pay_off_credit_cards', label: 'Pay Off Credit Cards', icon: '💳' },
  { id: 'pay_off_loans', label: 'Pay Off Loans', icon: '🏦' },
  { id: 'save_for_house', label: 'Save for House Down Payment', icon: '🏠' },
  { id: 'plan_retirement', label: 'Plan for Retirement', icon: '🏖️' },
  { id: 'invest_growth', label: 'Start Investing / Build Wealth', icon: '📈' },
  { id: 'save_education', label: 'Save for Education', icon: '🎓' },
  { id: 'start_business', label: 'Start a Business', icon: '💼' },
  { id: 'major_purchase', label: 'Save for Major Purchase', icon: '🚗' },
  { id: 'financial_independence', label: 'Achieve Financial Independence', icon: '🎯' },
  { id: 'major_life_event', label: 'Save for Major Life Event', icon: '✈️' },
  { id: 'other', label: 'Other Custom Goal', icon: '💎' }
];

// Education levels
const EDUCATION_LEVELS = [
  { value: 'high_school', label: 'High School / GED' },
  { value: 'some_college', label: 'Some College' },
  { value: 'associates', label: "Associate's Degree" },
  { value: 'bachelors', label: "Bachelor's Degree" },
  { value: 'masters', label: "Master's Degree" },
  { value: 'professional', label: 'Professional Degree (JD, MD, etc.)' },
  { value: 'doctorate', label: 'Doctorate' }
];

// US States
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

// Major US Cities
const MAJOR_CITIES = [
  'New York, NY', 'Los Angeles, CA', 'Chicago, IL', 'Houston, TX', 'Phoenix, AZ',
  'Philadelphia, PA', 'San Antonio, TX', 'San Diego, CA', 'Dallas, TX', 'San Jose, CA',
  'Austin, TX', 'Jacksonville, FL', 'Fort Worth, TX', 'Columbus, OH', 'Charlotte, NC',
  'San Francisco, CA', 'Indianapolis, IN', 'Seattle, WA', 'Denver, CO', 'Washington, DC',
  'Boston, MA', 'El Paso, TX', 'Nashville, TN', 'Detroit, MI', 'Oklahoma City, OK',
  'Portland, OR', 'Las Vegas, NV', 'Memphis, TN', 'Louisville, KY', 'Baltimore, MD'
];

const CreateJourneyModal = ({ open, onClose, onSuccess }) => {
  const { user } = useAuthContext();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  
  // Partner support: view selector state
  const [activeViewTab, setActiveViewTab] = useState('my'); // 'my', 'our', 'partner'
  const [partnerPermissions, setPartnerPermissions] = useState(null);
  
  // Derive partner mode flags
  const isPartnerMode = activeViewTab === 'partner';
  const isOurMode = activeViewTab === 'our';
  const hasPartner = user?.partner || user?.partnerId || user?.createdPartner;
  const partnerDisplayName = user?.partner?.firstName || user?.createdPartner?.firstName || 'Partner';

  // Step 1: Goals
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [customGoal, setCustomGoal] = useState('');

  // Step 2: Personal Information
  const [personalInfo, setPersonalInfo] = useState({
    startingAge: '',
    educationLevel: '',
    locationType: 'city',
    city: '',
    state: '',
    zipcode: '',
    openToMove: false,
    spendingStyle: 'moderate',
    familyStatus: 'single',
    childrenCount: 0,
    planningChildren: false,
    riskTolerance: 'moderate'
  });

  // Step 3: Income & Career
  const [incomes, setIncomes] = useState([]);
  const [selectedIncomeIds, setSelectedIncomeIds] = useState(new Set());
  const [incomeData, setIncomeData] = useState({
    useExisting: false,
    grossAnnual: '',
    netMonthly: '',
    careerField: '',
    careerStatus: 'has-career',
    customCareer: '',
    employmentStatus: 'full-time'
  });

  // Step 4: Current Financials
  const [financials, setFinancials] = useState({
    savings: '',
    monthlyExpenses: '',
    monthlySavingsRate: ''
  });
  const [debts, setDebts] = useState([]);
  const [debtPayoffMethod, setDebtPayoffMethod] = useState('avalanche');
  const [debtPayoffTab, setDebtPayoffTab] = useState(0);
  const [financialsAutoFilled, setFinancialsAutoFilled] = useState(false);
  const [loadingFinancials, setLoadingFinancials] = useState(false);

  // Step 5: Goal Details
  const [goalDetails, setGoalDetails] = useState({
    emergency_fund: { targetAmount: '' },
    pay_off_credit_cards: { debts: [], priority: [] },
    pay_off_loans: { debts: [], priority: [] },
    save_for_house: { targetAmount: '', targetDate: '', downPaymentPercent: 20 },
    plan_retirement: { targetAge: '', targetAmount: '' },
    invest_growth: { targetAmount: '', targetDate: '' },
    save_education: { forWhom: 'self', targetAmount: '', targetDate: '', beneficiaryName: '' },
    start_business: { initialCapital: '', targetDate: '', businessType: '' },
    major_purchase: { item: '', targetAmount: '', targetDate: '' },
    financial_independence: { targetAmount: '', targetAge: '' },
    major_life_event: { eventType: '', targetAmount: '', targetDate: '' },
    other: { description: '', targetAmount: '', targetDate: '' }
  });

  // Step 6: Review
  const [journeyName, setJourneyName] = useState('');

  // Fetch income data
  useEffect(() => {
    if (open && activeStep === 2) {
      fetchIncomeData();
    }
  }, [open, activeStep, activeViewTab]); // Re-fetch when view changes

  // Reset auto-fill flag when modal opens
  useEffect(() => {
    if (open) {
      setFinancialsAutoFilled(false);
    }
  }, [open]);

  // Fetch financial data when reaching Financials step
  useEffect(() => {
    if (open && activeStep === 3 && !financialsAutoFilled) {
      fetchFinancialData();
    }
  }, [open, activeStep]);

  // Generate journey name when reaching review step
  useEffect(() => {
    if (activeStep === 5 && !journeyName) {
      const generatedName = generateJourneyName();
      setJourneyName(generatedName);
    }
  }, [activeStep, journeyName]);

  // Fetch partner permissions
  useEffect(() => {
    const fetchPartnerPermissions = async () => {
      if (!hasPartner) return;
      
      try {
        let ownerUserId;
        if (user?.partnerId) {
          ownerUserId = user.partnerId;
        } else if (user?.createdPartner) {
          ownerUserId = user._id;
        } else {
          return;
        }

        const axiosInstance = (await import('../../../utils/axiosConfig')).default;
        const response = await axiosInstance.get(`/api/user-sharing/partner-permissions/${ownerUserId}`);
        if (response.data?.success) {
          setPartnerPermissions(response.data.permissions);
        }
      } catch (error) {
        // Silently handle 404 - partner permissions endpoint may not exist for all users
        if (error.response?.status !== 404) {
          console.error('Error fetching partner permissions:', error);
        }
      }
    };

    if (open && hasPartner) {
      fetchPartnerPermissions();
    }
  }, [open, hasPartner, user]);

  const fetchIncomeData = async () => {
    try {
      // Use partner options like JourneyDetail
      const incomeOptions = {
        partnerAccess: isPartnerMode,
        includePartner: isOurMode && !isPartnerMode
      };
      
      const response = await incomeService.getAll(incomeOptions);
      const incomesArray = response?.data || response || [];
      setIncomes(incomesArray);
      
      // Default all incomes to selected
      const allIncomeIds = new Set(incomesArray.map(income => income._id));
      setSelectedIncomeIds(allIncomeIds);
    } catch (error) {
      console.error('Error fetching income:', error);
    }
  };

  const fetchFinancialData = async () => {
    try {
      setLoadingFinancials(true);
      
      // Fetch savings data
      let totalSavings = 0;
      try {
        const savingsData = await savingsService.getAllSavings();
        if (Array.isArray(savingsData)) {
          totalSavings = savingsData.reduce((sum, savings) => {
            // Sum up current balances from savings accounts
            const balance = savings.currentBalance || savings.balance || savings.targetAmount || 0;
            return sum + (parseFloat(balance) || 0);
          }, 0);
        }
      } catch (error) {
        console.error('Error fetching savings:', error);
      }

      // Fetch expense statistics
      let monthlyExpenses = 0;
      try {
        const expenseStats = await expenseService.getExpenseStatistics();
        if (expenseStats && expenseStats.totalMonthly) {
          monthlyExpenses = expenseStats.totalMonthly;
        }
      } catch (error) {
        console.error('Error fetching expenses:', error);
      }

      // Calculate monthly savings rate
      // Get net monthly income from selected incomes or manual input
      let netMonthlyIncome = 0;
      if (incomeData.useExisting && selectedIncomeIds.size > 0) {
        const selectedIncomes = incomes.filter(inc => selectedIncomeIds.has(inc._id));
        selectedIncomes.forEach(income => {
          const amount = income.netAmount || income.amount || 0;
          const frequency = income.frequency || 'monthly';
          let monthlyAmount = amount;
          if (frequency === 'weekly') monthlyAmount = amount * 4.33;
          else if (frequency === 'bi-weekly' || frequency === 'biweekly') monthlyAmount = amount * 2.17;
          else if (frequency === 'yearly') monthlyAmount = amount / 12;
          else if (frequency === 'semi-monthly' || frequency === 'semimonthly') monthlyAmount = amount * 2;
          netMonthlyIncome += monthlyAmount;
        });
      } else if (incomeData.netMonthly) {
        netMonthlyIncome = parseFloat(incomeData.netMonthly) || 0;
      }

      // Calculate savings rate: (income - expenses) / income * 100
      // Only calculate if we have both income and expenses data
      let monthlySavingsRate = 0;
      if (netMonthlyIncome > 0 && monthlyExpenses > 0) {
        const savingsAmount = netMonthlyIncome - monthlyExpenses;
        monthlySavingsRate = (savingsAmount / netMonthlyIncome) * 100;
        // Ensure it's not negative
        if (monthlySavingsRate < 0) monthlySavingsRate = 0;
        // Cap at 100%
        if (monthlySavingsRate > 100) monthlySavingsRate = 100;
      }
      // If expenses are 0 or missing, don't calculate - leave empty for user to enter

      // Auto-fill fields if we have data and fields are empty
      // Show banner if we attempted to fetch data, even if values are 0
      const fieldsEmpty = !financials.savings && !financials.monthlyExpenses && !financials.monthlySavingsRate;
      
      if (fieldsEmpty) {
        setFinancials(prev => ({
          ...prev,
          savings: totalSavings > 0 ? totalSavings.toFixed(2) : '',
          monthlyExpenses: monthlyExpenses > 0 ? monthlyExpenses.toFixed(2) : '',
          monthlySavingsRate: monthlySavingsRate > 0 ? monthlySavingsRate.toFixed(2) : ''
        }));
        // Show banner if we fetched data (even if values are 0, to indicate we tried)
        if (totalSavings !== undefined || monthlyExpenses !== undefined || monthlySavingsRate !== undefined) {
          setFinancialsAutoFilled(true);
        }
      }
    } catch (error) {
      console.error('Error fetching financial data:', error);
    } finally {
      setLoadingFinancials(false);
    }
  };

  const handleGoalToggle = (goalId) => {
    if (goalId === 'other') {
      // Handle custom goal separately
      return;
    }
    
    setSelectedGoals(prev => 
      prev.includes(goalId)
        ? prev.filter(id => id !== goalId)
        : [...prev, goalId]
    );
  };

  const handleNext = () => {
    // Validate current step
    if (activeStep === 0 && selectedGoals.length === 0) {
      toast.error('Please select at least one goal');
      return;
    }
    
    if (activeStep === 1) {
      if (!personalInfo.startingAge || !personalInfo.educationLevel) {
        toast.error('Please fill in all required fields');
        return;
      }
    }

    setCompletedSteps(prev => new Set([...prev, activeStep]));
    
    if (activeStep < STEPS.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleCreate = async () => {
    try {
      setLoading(true);

      // Calculate net monthly income
      let netMonthly = 0;
      if (incomeData.useExisting && selectedIncomeIds.size > 0) {
        const selectedIncomes = incomes.filter(inc => selectedIncomeIds.has(inc._id));
        selectedIncomes.forEach(income => {
          const amount = income.netAmount || income.amount || 0;
          const frequency = income.frequency || 'monthly';
          let monthlyAmount = amount;
          if (frequency === 'weekly') monthlyAmount = amount * 4.33;
          else if (frequency === 'bi-weekly' || frequency === 'biweekly') monthlyAmount = amount * 2.17;
          else if (frequency === 'yearly') monthlyAmount = amount / 12;
          else if (frequency === 'semi-monthly' || frequency === 'semimonthly') monthlyAmount = amount * 2;
          netMonthly += monthlyAmount;
        });
      } else {
        netMonthly = parseFloat(incomeData.netMonthly) || 0;
      }

      const journeyData = {
        name: journeyName,
        goals: selectedGoals,
        startingAge: parseInt(personalInfo.startingAge),
        educationLevel: personalInfo.educationLevel,
        location: {
          type: personalInfo.locationType,
          city: personalInfo.city,
          state: personalInfo.state,
          zipcode: personalInfo.zipcode,
          openToMove: personalInfo.openToMove
        },
        lifestyle: {
          spendingStyle: personalInfo.spendingStyle,
          familyStatus: personalInfo.familyStatus,
          children: {
            count: personalInfo.childrenCount,
            planning: personalInfo.planningChildren
          },
          riskTolerance: personalInfo.riskTolerance
        },
        income: {
          grossAnnual: parseFloat(incomeData.grossAnnual) || 0,
          netMonthly: netMonthly,
          source: incomeData.useExisting ? 'existing-data' : 'manual',
          selectedIncomeIds: Array.from(selectedIncomeIds)
        },
        career: {
          field: incomeData.careerField,
          status: incomeData.careerStatus,
          customField: incomeData.customCareer,
          employmentStatus: incomeData.employmentStatus
        },
        currentFinancials: {
          savings: parseFloat(financials.savings) || 0,
          debts: debts.map(debt => ({
            debtId: debt._id,
            type: debt.type,
            name: debt.name,
            balance: debt.balance,
            interestRate: debt.interestRate,
            minimumPayment: debt.minimumPayment,
            priority: debt.priority
          })),
          monthlyExpenses: parseFloat(financials.monthlyExpenses) || 0,
          monthlySavingsRate: parseFloat(financials.monthlySavingsRate) || 0
        },
        debtPayoffMethod: debtPayoffMethod,
        goalDetails: goalDetails
      };

      onSuccess(journeyData);
    } catch (error) {
      console.error('Error creating journey:', error);
      toast.error('Failed to create journey');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderGoalsStep();
      case 1:
        return renderPersonalInfoStep();
      case 2:
        return renderIncomeCareerStep();
      case 3:
        return renderFinancialsStep();
      case 4:
        return renderGoalDetailsStep();
      case 5:
        return renderReviewStep();
      default:
        return null;
    }
  };

  const renderGoalsStep = () => (
    <Box sx={{ minHeight: '400px' }}>
      <Typography variant="h6" gutterBottom>
        What are your financial goals?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select all that apply. You can track multiple goals simultaneously.
      </Typography>
      {GOAL_OPTIONS && GOAL_OPTIONS.length > 0 ? (
        <Grid container spacing={2}>
          {GOAL_OPTIONS.map((goal) => (
            <Grid item xs={12} sm={6} md={4} key={goal.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: selectedGoals.includes(goal.id) ? 2 : 1,
                  borderColor: selectedGoals.includes(goal.id) ? 'primary.main' : 'grey.300',
                  bgcolor: selectedGoals.includes(goal.id) ? 'primary.50' : 'white',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'primary.50',
                    transform: 'translateY(-2px)',
                    boxShadow: 2
                  }
                }}
                onClick={() => handleGoalToggle(goal.id)}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Checkbox
                      checked={selectedGoals.includes(goal.id)}
                      onChange={() => handleGoalToggle(goal.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Typography variant="h5" component="span" sx={{ fontSize: '1.5rem' }}>
                      {goal.icon}
                    </Typography>
                    <Typography variant="body1" sx={{ flex: 1, fontWeight: selectedGoals.includes(goal.id) ? 600 : 400 }}>
                      {goal.label}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No goals available. Please refresh the page.
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderPersonalInfoStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Personal Information
      </Typography>
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Starting Age *"
            type="number"
            value={personalInfo.startingAge}
            onChange={(e) => setPersonalInfo(prev => ({ ...prev, startingAge: e.target.value }))}
            inputProps={{ min: 18, max: 100 }}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Education Level *</InputLabel>
            <Select
              value={personalInfo.educationLevel}
              onChange={(e) => setPersonalInfo(prev => ({ ...prev, educationLevel: e.target.value }))}
              label="Education Level *"
            >
              {EDUCATION_LEVELS.map(level => (
                <MenuItem key={level.value} value={level.value}>{level.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {/* Location */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>Location</Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Location Type</InputLabel>
            <Select
              value={personalInfo.locationType}
              onChange={(e) => setPersonalInfo(prev => ({ ...prev, locationType: e.target.value }))}
              label="Location Type"
            >
              <MenuItem value="city">Major City</MenuItem>
              <MenuItem value="state-city">State + City</MenuItem>
              <MenuItem value="zipcode">Zipcode</MenuItem>
              <MenuItem value="open-to-move">Open to Move</MenuItem>
            </Select>
          </FormControl>
          
          {personalInfo.locationType === 'city' && (
            <Autocomplete
              options={MAJOR_CITIES}
              value={personalInfo.city}
              onChange={(e, newValue) => setPersonalInfo(prev => ({ ...prev, city: newValue || '' }))}
              renderInput={(params) => <TextField {...params} label="Select City" />}
            />
          )}
          
          {personalInfo.locationType === 'state-city' && (
            <>
              <Autocomplete
                options={US_STATES}
                value={personalInfo.state}
                onChange={(e, newValue) => setPersonalInfo(prev => ({ ...prev, state: newValue || '' }))}
                renderInput={(params) => <TextField {...params} label="State" sx={{ mb: 2 }} />}
              />
              <TextField
                fullWidth
                label="City"
                value={personalInfo.city}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, city: e.target.value }))}
              />
            </>
          )}
          
          {personalInfo.locationType === 'zipcode' && (
            <TextField
              fullWidth
              label="Zipcode"
              value={personalInfo.zipcode}
              onChange={(e) => setPersonalInfo(prev => ({ ...prev, zipcode: e.target.value }))}
            />
          )}
          
          {personalInfo.locationType === 'open-to-move' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={personalInfo.openToMove}
                  onChange={(e) => setPersonalInfo(prev => ({ ...prev, openToMove: e.target.checked }))}
                />
              }
              label="I'm open to moving for better opportunities"
            />
          )}
        </Grid>

        {/* Lifestyle */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Spending Style</InputLabel>
            <Select
              value={personalInfo.spendingStyle}
              onChange={(e) => setPersonalInfo(prev => ({ ...prev, spendingStyle: e.target.value }))}
              label="Spending Style"
            >
              <MenuItem value="frugal">Frugal</MenuItem>
              <MenuItem value="moderate">Moderate</MenuItem>
              <MenuItem value="comfortable">Comfortable</MenuItem>
              <MenuItem value="luxury">Luxury</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Family Status</InputLabel>
            <Select
              value={personalInfo.familyStatus}
              onChange={(e) => setPersonalInfo(prev => ({ ...prev, familyStatus: e.target.value }))}
              label="Family Status"
            >
              <MenuItem value="single">Single</MenuItem>
              <MenuItem value="married">Married</MenuItem>
              <MenuItem value="domestic_partner">Domestic Partner</MenuItem>
              <MenuItem value="divorced">Divorced</MenuItem>
              <MenuItem value="widowed">Widowed</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Number of Children"
            type="number"
            value={personalInfo.childrenCount}
            onChange={(e) => setPersonalInfo(prev => ({ ...prev, childrenCount: parseInt(e.target.value) || 0 }))}
            inputProps={{ min: 0 }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={personalInfo.planningChildren}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, planningChildren: e.target.checked }))}
              />
            }
            label="Planning to have children"
          />
        </Grid>
        
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Risk Tolerance</InputLabel>
            <Select
              value={personalInfo.riskTolerance}
              onChange={(e) => setPersonalInfo(prev => ({ ...prev, riskTolerance: e.target.value }))}
              label="Risk Tolerance"
            >
              <MenuItem value="conservative">Conservative</MenuItem>
              <MenuItem value="moderate">Moderate</MenuItem>
              <MenuItem value="aggressive">Aggressive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );

  const renderIncomeCareerStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Income & Career
      </Typography>
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={incomeData.useExisting}
                onChange={(e) => setIncomeData(prev => ({ ...prev, useExisting: e.target.checked }))}
              />
            }
            label="Use my existing income data"
          />
        </Grid>

        {incomeData.useExisting ? (
          <Grid item xs={12}>
            {/* Partner View Selector */}
            {hasPartner && (
              <Box sx={{ mb: 3 }}>
                <MobileViewSelector
                  title="Income Source"
                  currentView={activeViewTab}
                  onViewChange={(view) => {
                    setActiveViewTab(view);
                  }}
                  views={[
                    {
                      value: 'my',
                      label: 'My Income',
                      icon: <UserIcon className="h-5 w-5" />
                    },
                    {
                      value: 'our',
                      label: 'Our Income',
                      icon: <UserGroupIcon className="h-5 w-5" />
                    },
                    {
                      value: 'partner',
                      label: `${partnerDisplayName}'s Income`,
                      icon: <UserIcon className="h-5 w-5" />,
                      disabled: !partnerPermissions?.hasFullCashflowAccess,
                      title: partnerPermissions?.hasFullCashflowAccess 
                        ? `View ${partnerDisplayName}'s income`
                        : 'Partner has not granted access'
                    }
                  ]}
                />
              </Box>
            )}
            
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Select Income Sources</Typography>
            {incomes.map((income) => (
              <FormControlLabel
                key={income._id}
                control={
                  <Checkbox
                    checked={selectedIncomeIds.has(income._id)}
                    onChange={(e) => {
                      const newSet = new Set(selectedIncomeIds);
                      if (e.target.checked) {
                        newSet.add(income._id);
                      } else {
                        newSet.delete(income._id);
                      }
                      setSelectedIncomeIds(newSet);
                    }}
                  />
                }
                label={`${income.name} - $${(income.netAmount || income.amount || 0).toLocaleString()}/${income.frequency || 'monthly'}`}
              />
            ))}
          </Grid>
        ) : (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Gross Annual Income"
                type="number"
                value={incomeData.grossAnnual}
                onChange={(e) => setIncomeData(prev => ({ ...prev, grossAnnual: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Net Monthly Income"
                type="number"
                value={incomeData.netMonthly}
                onChange={(e) => setIncomeData(prev => ({ ...prev, netMonthly: e.target.value }))}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Career Status</InputLabel>
            <Select
              value={incomeData.careerStatus}
              onChange={(e) => setIncomeData(prev => ({ ...prev, careerStatus: e.target.value }))}
              label="Career Status"
            >
              <MenuItem value="has-career">I have a career</MenuItem>
              <MenuItem value="no-career">I don't have a career yet</MenuItem>
              <MenuItem value="open-to-change">Open to changing careers</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {incomeData.careerStatus === 'has-career' && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Career Field"
              value={incomeData.careerField}
              onChange={(e) => setIncomeData(prev => ({ ...prev, careerField: e.target.value }))}
            />
          </Grid>
        )}

        {incomeData.careerStatus === 'open-to-change' && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Current Career / Desired Career"
              value={incomeData.customCareer}
              onChange={(e) => setIncomeData(prev => ({ ...prev, customCareer: e.target.value }))}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Employment Status</InputLabel>
            <Select
              value={incomeData.employmentStatus}
              onChange={(e) => setIncomeData(prev => ({ ...prev, employmentStatus: e.target.value }))}
              label="Employment Status"
            >
              <MenuItem value="full-time">Full-time</MenuItem>
              <MenuItem value="part-time">Part-time</MenuItem>
              <MenuItem value="self-employed">Self-employed</MenuItem>
              <MenuItem value="unemployed">Unemployed</MenuItem>
              <MenuItem value="student">Student</MenuItem>
              <MenuItem value="retired">Retired</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );

  const renderFinancialsStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Current Financial Situation
      </Typography>
      {financialsAutoFilled && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
          <Typography variant="body2" color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CheckCircleIcon sx={{ fontSize: 16 }} />
            {financials.savings || financials.monthlyExpenses || financials.monthlySavingsRate
              ? 'Data auto-filled from your existing financial information. You can edit these values if needed.'
              : 'Attempted to auto-fill from your financial data, but no data was found. Please enter your financial information manually.'}
          </Typography>
        </Box>
      )}
      {loadingFinancials && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Loading your financial data...
          </Typography>
        </Box>
      )}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Current Savings"
            type="number"
            value={financials.savings}
            onChange={(e) => setFinancials(prev => ({ ...prev, savings: e.target.value }))}
            helperText={financialsAutoFilled && financials.savings ? "Auto-filled from your savings accounts" : "Enter your total current savings"}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Monthly Expenses"
            type="number"
            value={financials.monthlyExpenses}
            onChange={(e) => setFinancials(prev => ({ ...prev, monthlyExpenses: e.target.value }))}
            helperText={financialsAutoFilled && financials.monthlyExpenses ? "Auto-filled from your expense data" : "Enter your total monthly expenses"}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Monthly Savings Rate (%)"
            type="number"
            value={financials.monthlySavingsRate}
            onChange={(e) => setFinancials(prev => ({ ...prev, monthlySavingsRate: e.target.value }))}
            helperText={financialsAutoFilled && financials.monthlySavingsRate ? "Auto-calculated from your income and expenses" : "Enter your monthly savings rate as a percentage"}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>
            }}
          />
        </Grid>

        {/* Debt Payoff Strategy Tabs */}
        {selectedGoals.includes('pay_off_credit_cards') || selectedGoals.includes('pay_off_loans') ? (
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Debt Payoff Strategy</Typography>
            <Tabs value={debtPayoffTab} onChange={(e, v) => setDebtPayoffTab(v)}>
              <Tab label="Avalanche (Highest Interest First)" />
              <Tab label="Snowball (Smallest Balance First)" />
              <Tab label="Custom Priority" />
            </Tabs>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              {debtPayoffTab === 0 && (
                <Typography variant="body2">
                  Pay off debts with the highest interest rates first. This method saves the most money on interest.
                </Typography>
              )}
              {debtPayoffTab === 1 && (
                <Typography variant="body2">
                  Pay off debts with the smallest balances first. This method provides quick wins and motivation.
                </Typography>
              )}
              {debtPayoffTab === 2 && (
                <Typography variant="body2">
                  Set your own priority order for paying off debts.
                </Typography>
              )}
            </Box>
            {debtPayoffTab === 0 && setDebtPayoffMethod('avalanche')}
            {debtPayoffTab === 1 && setDebtPayoffMethod('snowball')}
            {debtPayoffTab === 2 && setDebtPayoffMethod('custom')}
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );

  const renderGoalDetailsStep = () => {
    if (selectedGoals.length === 0) {
      return (
        <Box>
          <Typography variant="h6" gutterBottom>
            Goal Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No goals selected. Please go back and select at least one goal.
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Goal Details
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Provide specific details for your selected goals. This helps us create a more accurate plan.
        </Typography>

        <Grid container spacing={3}>
          {selectedGoals.map((goalId) => {
            const goal = GOAL_OPTIONS.find(g => g.id === goalId);
            if (!goal) return null;

            return (
              <Grid item xs={12} key={goalId}>
                <Card sx={{ p: 2, mb: 2 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Typography variant="h6" component="span">
                      {goal.icon}
                    </Typography>
                    <Typography variant="h6">
                      {goal.label}
                    </Typography>
                  </Box>

                  {goalId === 'emergency_fund' && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Target Amount"
                          type="number"
                          value={goalDetails.emergency_fund?.targetAmount || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            emergency_fund: { ...prev.emergency_fund, targetAmount: e.target.value }
                          }))}
                          helperText="Recommended: 3-6 months of expenses"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Target Months"
                          type="number"
                          value={goalDetails.emergency_fund?.targetMonths || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            emergency_fund: { ...prev.emergency_fund, targetMonths: e.target.value }
                          }))}
                          helperText="How many months of expenses to save"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">months</InputAdornment>
                          }}
                        />
                      </Grid>
                    </Grid>
                  )}

                  {goalId === 'save_for_house' && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Target Amount"
                          type="number"
                          value={goalDetails.save_for_house?.targetAmount || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            save_for_house: { ...prev.save_for_house, targetAmount: e.target.value }
                          }))}
                          helperText="Total down payment amount"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth
                          label="Down Payment %"
                          type="number"
                          value={goalDetails.save_for_house?.downPaymentPercent || 20}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            save_for_house: { ...prev.save_for_house, downPaymentPercent: e.target.value }
                          }))}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          fullWidth
                          label="Target Date"
                          type="date"
                          value={goalDetails.save_for_house?.targetDate || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            save_for_house: { ...prev.save_for_house, targetDate: e.target.value }
                          }))}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  )}

                  {goalId === 'plan_retirement' && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Target Age"
                          type="number"
                          value={goalDetails.plan_retirement?.targetAge || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            plan_retirement: { ...prev.plan_retirement, targetAge: e.target.value }
                          }))}
                          helperText="Age you want to retire"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">years</InputAdornment>
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Target Amount"
                          type="number"
                          value={goalDetails.plan_retirement?.targetAmount || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            plan_retirement: { ...prev.plan_retirement, targetAmount: e.target.value }
                          }))}
                          helperText="Total retirement savings goal"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                          }}
                        />
                      </Grid>
                    </Grid>
                  )}

                  {goalId === 'financial_independence' && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="FIRE Number"
                          type="number"
                          value={goalDetails.financial_independence?.targetAmount || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            financial_independence: { ...prev.financial_independence, targetAmount: e.target.value }
                          }))}
                          helperText="Your Financial Independence number (25x annual expenses)"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Target Age"
                          type="number"
                          value={goalDetails.financial_independence?.targetAge || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            financial_independence: { ...prev.financial_independence, targetAge: e.target.value }
                          }))}
                          helperText="Age you want to achieve FIRE"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">years</InputAdornment>
                          }}
                        />
                      </Grid>
                    </Grid>
                  )}

                  {goalId === 'save_education' && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <InputLabel>For Whom</InputLabel>
                          <Select
                            value={goalDetails.save_education?.forWhom || 'self'}
                            onChange={(e) => setGoalDetails(prev => ({
                              ...prev,
                              save_education: { ...prev.save_education, forWhom: e.target.value }
                            }))}
                            label="For Whom"
                          >
                            <MenuItem value="self">Self</MenuItem>
                            <MenuItem value="children">Children</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Target Amount"
                          type="number"
                          value={goalDetails.save_education?.targetAmount || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            save_education: { ...prev.save_education, targetAmount: e.target.value }
                          }))}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Target Date"
                          type="date"
                          value={goalDetails.save_education?.targetDate || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            save_education: { ...prev.save_education, targetDate: e.target.value }
                          }))}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  )}

                  {goalId === 'start_business' && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Business Type"
                          value={goalDetails.start_business?.businessType || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            start_business: { ...prev.start_business, businessType: e.target.value }
                          }))}
                          helperText="e.g., Tech startup, Restaurant, Consulting"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Initial Capital Needed"
                          type="number"
                          value={goalDetails.start_business?.initialCapital || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            start_business: { ...prev.start_business, initialCapital: e.target.value }
                          }))}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Target Date"
                          type="date"
                          value={goalDetails.start_business?.targetDate || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            start_business: { ...prev.start_business, targetDate: e.target.value }
                          }))}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  )}

                  {goalId === 'major_purchase' && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Item"
                          value={goalDetails.major_purchase?.item || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            major_purchase: { ...prev.major_purchase, item: e.target.value }
                          }))}
                          helperText="e.g., Car, Vacation, Home Renovation"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Target Amount"
                          type="number"
                          value={goalDetails.major_purchase?.targetAmount || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            major_purchase: { ...prev.major_purchase, targetAmount: e.target.value }
                          }))}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Target Date"
                          type="date"
                          value={goalDetails.major_purchase?.targetDate || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            major_purchase: { ...prev.major_purchase, targetDate: e.target.value }
                          }))}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  )}

                  {goalId === 'major_life_event' && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Event Type"
                          value={goalDetails.major_life_event?.eventType || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            major_life_event: { ...prev.major_life_event, eventType: e.target.value }
                          }))}
                          helperText="e.g., Wedding, Travel, Moving"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Target Amount"
                          type="number"
                          value={goalDetails.major_life_event?.targetAmount || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            major_life_event: { ...prev.major_life_event, targetAmount: e.target.value }
                          }))}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Target Date"
                          type="date"
                          value={goalDetails.major_life_event?.targetDate || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            major_life_event: { ...prev.major_life_event, targetDate: e.target.value }
                          }))}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  )}

                  {goalId === 'other' && (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Goal Description"
                          value={goalDetails.other?.description || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            other: { ...prev.other, description: e.target.value }
                          }))}
                          multiline
                          rows={2}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Target Amount"
                          type="number"
                          value={goalDetails.other?.targetAmount || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            other: { ...prev.other, targetAmount: e.target.value }
                          }))}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Target Date"
                          type="date"
                          value={goalDetails.other?.targetDate || ''}
                          onChange={(e) => setGoalDetails(prev => ({
                            ...prev,
                            other: { ...prev.other, targetDate: e.target.value }
                          }))}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  )}

                  {(goalId === 'pay_off_credit_cards' || goalId === 'pay_off_loans' || goalId === 'invest_growth') && (
                    <Typography variant="body2" color="text.secondary">
                      Details for this goal will be calculated automatically based on your financial data.
                    </Typography>
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  const renderReviewStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review & Generate
      </Typography>
      <TextField
        fullWidth
        label="Journey Name"
        value={journeyName}
        onChange={(e) => setJourneyName(e.target.value)}
        sx={{ mb: 3 }}
      />
      <Typography variant="body2" color="text.secondary">
        Review your information and click "Create Journey" to generate your personalized financial plan.
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '600px',
          borderRadius: 3
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" sx={{ fontWeight: 'bold', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Create Financial Journey
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* Progress Bar */}
        <Box sx={{ mb: 4, mt: 2 }}>
          <LinearProgress 
            variant="determinate" 
            value={((activeStep + 1) / STEPS.length) * 100} 
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            {STEPS.map((step, index) => (
              <Typography
                key={index}
                variant="caption"
                color={index <= activeStep ? 'primary' : 'text.secondary'}
                sx={{ fontWeight: index === activeStep ? 'bold' : 'normal' }}
              >
                {step.label}
              </Typography>
            ))}
          </Box>
        </Box>
        
        <Box sx={{ minHeight: '400px', py: 2 }}>
          {renderStepContent()}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleBack}
          disabled={activeStep === 0}
          startIcon={<ArrowBackIcon />}
        >
          Back
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {activeStep === STEPS.length - 1 ? (
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3d91 100%)'
              }
            }}
          >
            {loading ? 'Creating...' : 'Create Journey'}
          </Button>
        ) : (
          <Button
            onClick={handleNext}
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3d91 100%)'
              }
            }}
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateJourneyModal;

